import { useState } from "react";
import { Button } from "../../components/Button";
import { ApiError, api } from "../../lib/api";
import type { CameraResponse } from "../../lib/api";
import styles from "./CameraView.module.css";

export type SamplingControlProps = {
  camera: CameraResponse;
  onChanged: (camera: CameraResponse) => void;
};

// Desired-vs-actual (ADR 0017): is_enabled is the edge-reported actual; toggling records a
// desired state the edge applies and confirms via its roster report.
const pendingConfirmation = (camera: CameraResponse): boolean =>
  camera.desired_enabled !== null &&
  camera.desired_enabled !== camera.is_enabled &&
  (camera.desired_status === "pending" || camera.desired_status === "enqueued");

export const SamplingControl = ({ camera, onChanged }: SamplingControlProps) => {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const pending = pendingConfirmation(camera);
  const failed = camera.desired_status === "failed" && camera.desired_enabled !== camera.is_enabled;
  const target = pending ? !camera.desired_enabled : !camera.is_enabled;

  const toggle = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      onChanged(await api.updateCamera(camera.id, { is_enabled: target }));
    } catch (e) {
      setError(e instanceof ApiError ? (e.body?.error ?? e.message) : "Couldn't update sampling.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={styles.field}>
      <span className={styles.label}>Sampling</span>
      <span className={styles.hint}>
        {pending
          ? camera.desired_enabled
            ? "Enabling… pending edge confirmation"
            : "Disabling… pending edge confirmation"
          : camera.is_enabled
            ? "Enabled"
            : "Disabled"}
      </span>
      {failed && (
        <span role="alert" className={styles.error}>
          {camera.desired_enabled ? "Enable" : "Disable"} request failed
          {camera.desired_last_error ? ` — ${camera.desired_last_error}` : ""}. Try again.
        </span>
      )}
      {error && (
        <span role="alert" className={styles.error}>
          {error}
        </span>
      )}
      <Button variant="outline" size="sm" onClick={() => void toggle()} disabled={busy}>
        {busy ? "Saving…" : target ? "Enable" : "Disable"}
      </Button>
    </div>
  );
};
